import { ArrowUpRight, CalendarClock, KanbanSquare, PenLine, Trophy } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";

import { api } from "@/api";
import { euros, frDate, initials } from "@/components/pipeline/utils";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
export const monthLabel = (ym) => MONTHS[Number(ym.slice(5, 7)) - 1];
const plural = (n, w) => `${n.toLocaleString("fr-FR")} ${w}${n > 1 ? "s" : ""}`;

export function Kpis({ d }) {
  const { count, amount } = d.signed;
  const prev = d.signed_prev.count;
  const delta = prev ? Math.round(((count - prev) / prev) * 100) : null;
  const items = [
    { k: "signed", icon: Trophy, value: count, unit: count > 1 ? " signatures" : " signature", badge: delta !== null ? `${delta >= 0 ? "+" : ""}${delta} % vs ${monthLabel(d.signed_prev.month)}` : null, tone: delta !== null && delta < 0 ? "yellow" : "green", sub: `Signé ce mois${amount ? ` · ${euros(amount)}` : ""}` },
    { k: "open", icon: KanbanSquare, value: d.open.count, sub: `Pipeline ouvert${d.open.amount ? ` · ${euros(d.open.amount)}` : ""}` },
    { k: "hot", icon: CalendarClock, value: d.hot.length, sub: "Affaires chaudes récentes" },
  ];
  return (
    <div className="head__stats">
      {items.map(({ k, icon: Icon, value, unit, badge, tone, sub }) => (
        <div key={k} className="stat" data-file="" data-testid={`home-kpi-${k}`}>
          <span className="stat__ico"><Icon size={18} /></span>
          <div>
            <div className="stat__value" data-testid={`home-kpi-${k}-value`}><span><b data-count={value}>{value.toLocaleString("fr-FR")}</b>{unit || ""}</span>{badge && <span className={`badge badge--${tone === "green" ? "pine" : "butter"}`} data-testid={`home-kpi-${k}-badge`}>{badge}</span>}</div>
            <div className="stat__label">{sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const EndDot = ({ cx, cy, index, points }) => (index === points - 1 ? <circle cx={cx} cy={cy} r={6} fill="#D6512A" stroke="var(--float)" strokeWidth={3} /> : null);

export function RevenueCurve({ d }) {
  const data = d.curve.map((c) => ({ ...c, label: monthLabel(c.month) }));
  const total = data.reduce((n, c) => n + c.count, 0);
  const target = d.objective.target;
  return (
    <section className="card hm-card hm-curve" data-land="" data-testid="home-curve">
      <header><h3>Signatures sur 12 mois</h3><small>{d.scope === "me" ? "Vos devis signés et au-delà" : "Devis signés et au-delà · toute l'équipe"}</small>
        <p className="hm-rule" data-testid="home-signed-rule">Une fiche compte comme signée dès qu'elle atteint l'étape « Devis signé » (ou une étape suivante) ou qu'elle est gagnée.{d.scope === "team" && <> <a href="/parametres-pipeline" data-testid="home-signed-settings">Régler les étapes</a></>}</p></header>
      <p className="hm-big" data-testid="home-curve-total">{plural(total, "signature")}</p>
      <div className="hm-legend"><span><i className="dot orange" />Signatures par mois</span>{target && d.scope === "team" && <span><i className="dash" />Objectif mensuel ({target})</span>}</div>
      <div className="hm-chart">
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={data} margin={{ top: 12, right: 14, left: -18, bottom: 0 }}>
            <defs><linearGradient id="hmFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D6512A" stopOpacity={0.22} /><stop offset="100%" stopColor="#D6512A" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid vertical={false} stroke="var(--line)" strokeDasharray="0" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#8B887F", fontSize: 12 }} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: "#8B887F", fontSize: 12 }} />
            <Tooltip formatter={(v) => [v, "Signatures"]} labelStyle={{ color: "var(--text)" }} contentStyle={{ background: "var(--float)", borderRadius: 14, border: 0, boxShadow: "0 8px 24px rgba(40,35,20,.12)" }} />
            {target && d.scope === "team" && <ReferenceLine y={target} stroke="var(--text)" strokeDasharray="5 5" />}
            <Area type="monotone" dataKey="count" stroke="#D6512A" strokeWidth={2.5} fill="url(#hmFill)" dot={<EndDot points={data.length} />} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export const STAGES = [
  { k: "qualif", label: "Qualification", tone: "yellow" },
  { k: "devis", label: "Devis", tone: "green" },
  { k: "nego", label: "Négociation", tone: "orange" },
  { k: "signed", label: "Signées", tone: "black" },
];
const toneOf = (group) => (STAGES.find((x) => x.k === group) || STAGES[0]).tone;

export function StageBar({ stages, scope, open }) {
  const total = STAGES.reduce((n, x) => n + stages[x.k].count, 0);
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <section className="card hm-card hm-stages" data-land="" data-testid="home-stages">
      <header><h3>Répartition par étape</h3><small data-testid="home-stages-total">{total.toLocaleString("fr-FR")} affaires actives sur {open.toLocaleString("fr-FR")} ouvertes{scope === "me" ? " · les vôtres" : ""}</small></header>
      <div className="hm-bar" role="img" aria-label={STAGES.map((x) => `${x.label} ${pct(stages[x.k].count)} %`).join(", ")}>
        {STAGES.filter((x) => stages[x.k].count).map((x) => <span key={x.k} className={`tone-${x.tone}`} style={{ flexGrow: stages[x.k].count }} title={`${x.label} : ${stages[x.k].count}`} data-testid={`home-stage-bar-${x.k}`} />)}
      </div>
      <div className="hm-stage-legend">
        {STAGES.map((x) => (
          <div key={x.k} data-testid={`home-stage-${x.k}`}>
            <i className={`tone-${x.tone}`} />
            <span>{x.label}</span>
            <strong>{stages[x.k].count.toLocaleString("fr-FR")}</strong>
            <small>{pct(stages[x.k].count)} %{stages[x.k].amount ? ` · ${euros(stages[x.k].amount)}` : ""}</small>
          </div>
        ))}
      </div>
      <p className="hm-note">Les {(open - total).toLocaleString("fr-FR")} autres sont dans des étapes d'abandon (annulé, plus intéressé, hors critères, NRP…).</p>
    </section>
  );
}

export function HotDeals({ deals, today }) {
  const go = useNavigate();
  return (
    <section className="card hm-card" data-land="" data-testid="home-hot">
      <header><h3>Affaires chaudes</h3><Link to="/pipeline" className="hm-round" title="Ouvrir le pipeline" data-testid="home-hot-open-pipeline"><ArrowUpRight size={14} /></Link></header>
      {!deals.length ? <p className="ck-muted">Aucune opportunité ouverte dans les deux étapes qui précèdent la signature.</p> : (
        <div className="hm-deals">
          {deals.map((l) => (
            <button key={l.lead_id} type="button" className={`hm-deal tone-${toneOf(l.group)}`} data-deal="" data-lift="" onClick={() => go(`/pipeline?lead=${l.lead_id}`)} data-testid={`home-deal-${l.lead_id}`}>
              <small>{l.remind_date ? `Prochaine action · ${frDate(l.remind_date)}` : `Mis à jour · ${frDate(l.updated_at)}`}{l.remind_date && l.remind_date < today && <span className="hm-late" data-testid={`home-deal-late-${l.lead_id}`}>en retard</span>}</small>
              <strong>{l.title}</strong>
              <span className="hm-deal-org">{l.pipeline}</span>
              <span className="hm-deal-foot">
                <span><b>{l.amount ? euros(l.amount) : l.step}</b>{l.amount ? <em>{l.step}</em> : null}</span>
                <span className="hm-ava" title={l.owner_name || "Non attribuée"}>{initials(l.owner_name)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

const arc = (pct) => {
  const a = Math.PI * (1 - Math.min(pct, 1));
  return `M 20 110 A 90 90 0 0 1 ${110 + 90 * Math.cos(a)} ${110 - 90 * Math.sin(a)}`;
};

export function ObjectiveGauge({ objective, canEdit, onSaved }) {
  const { target, done } = objective;
  const [edit, setEdit] = useState(false);
  const [value, setValue] = useState(target || "");
  const pct = target ? done / target : 0;
  const save = async () => {
    try { await api.put("/home/objective", { monthly_target: Number(value) }); setEdit(false); onSaved(); toast.success("Objectif enregistré."); }
    catch { toast.error("Objectif non enregistré : saisissez un nombre entier."); }
  };
  return (
    <section className="card hm-card hm-goal" data-land="" data-testid="home-objective">
      <header><h3>Objectif équipe · {monthLabel(new Date().toISOString().slice(0, 7))}</h3>{canEdit && !edit && <button type="button" className="hm-round light" onClick={() => setEdit(true)} title="Modifier l'objectif" data-testid="home-objective-edit"><PenLine size={14} /></button>}</header>
      {edit ? (
        <div className="hm-goal-form">
          <label>Signatures visées par mois<input type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)} data-testid="home-objective-input" /></label>
          <div><button type="button" className="hm-pill yellow" onClick={save} disabled={!value} data-testid="home-objective-save">Enregistrer</button><button type="button" className="hm-pill" onClick={() => setEdit(false)}>Annuler</button></div>
        </div>
      ) : (
        <>
          <svg viewBox="0 0 220 124" className="hm-gauge" aria-hidden="true">
            <path d="M 20 110 A 90 90 0 0 1 200 110" stroke="rgba(255,255,255,.16)" strokeWidth="16" fill="none" strokeLinecap="round" />
            {pct > 0 && <path d={arc(pct)} stroke="#E8C341" strokeWidth="16" fill="none" strokeLinecap="round" />}
          </svg>
          <p className="hm-goal-pct" data-testid="home-objective-pct">{target ? `${Math.round(pct * 100)} %` : "—"}</p>
          <p className="hm-goal-sub" data-testid="home-objective-text">{target ? `${plural(done, "signature")} sur ${target} ce mois-ci` : canEdit ? `${plural(done, "signature")} ce mois-ci, aucun objectif fixé.` : "Objectif du mois pas encore fixé."}</p>
          {!target && canEdit && <button type="button" className="hm-goal-cta" onClick={() => setEdit(true)} data-testid="home-objective-define"><PenLine size={14} /> Définir l'objectif</button>}
        </>
      )}
    </section>
  );
}
